import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { getVwapEntryConditions } from '../../api/tradeGroups';
import { useAutoRefreshTrigger } from '../../stores/useGlobalAutoRefreshStore';
import './VwapEntryConditionsBar.css';

/**
 * Props for VwapEntryConditionsBar component.
 * Diverges from sibling Bars (which take entryConditions as a prop) because the
 * VWAP bar owns its own fetch — TASK-040 specifies the component fetch the
 * conditions response via getVwapEntryConditions and refetch on the global
 * auto-refresh trigger.
 */
export interface VwapEntryConditionsBarProps {
  tradeGroupId: string;
  className?: string;
}

const STALE_TIME_MS = 30_000;
const DEVIATION_DECIMALS = 4;

/**
 * Clamp a numeric price position into the visible [0, 100] gauge range so an
 * out-of-band price marker still renders at the edge instead of overflowing.
 */
const clampPercent = (value: number): number => {
  if (value < 0) return 0;
  if (value > 100) return 100;
  return value;
};

/**
 * Map current_price to a horizontal gauge position (0..100) where 0 = lower
 * band, 50 = vwap, 100 = upper band. Returns null when any input is missing or
 * the band collapse (lower >= upper) would divide by zero.
 */
const calculateMarkerPercent = (
  currentPrice: number | null,
  lowerBand: number | null,
  upperBand: number | null,
): number | null => {
  if (currentPrice === null || lowerBand === null || upperBand === null) {
    return null;
  }
  if (upperBand <= lowerBand) {
    return null;
  }
  const pct = ((currentPrice - lowerBand) / (upperBand - lowerBand)) * 100;
  return clampPercent(pct);
};

/**
 * Sub-$1 assets (ADA, XRP, SUI, etc.) collapse to indistinguishable values at
 * 2 decimals — every gauge tick reads "$0.24" and the operator can't tell the
 * bands apart. Mirror the MacdExitConditionsBar convention: 4 decimals when
 * price < $1, 2 decimals otherwise.
 */
const formatPrice = (value: number | null): string => {
  if (value === null) return '—';
  if (Math.abs(value) < 1) {
    return `$${value.toFixed(4)}`;
  }
  return `$${value.toFixed(2)}`;
};

const formatDeviation = (value: number | null): string => {
  if (value === null) return '—';
  return `${value.toFixed(DEVIATION_DECIMALS)}%`;
};

const VwapEntryConditionsBar: React.FC<VwapEntryConditionsBarProps> = ({
  tradeGroupId,
  className = '',
}) => {
  // Mirrors ActiveTradeGroupsTable's dashboardId — both surfaces show per-card
  // trade-group indicators that refresh on the trade-groups dashboard tick.
  const refreshTrigger = useAutoRefreshTrigger('trade-groups');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['vwap-entry-conditions', tradeGroupId, refreshTrigger],
    queryFn: () => getVwapEntryConditions(tradeGroupId),
    staleTime: STALE_TIME_MS,
    retry: 1,
  });

  if (isError) {
    return null;
  }

  if (isLoading) {
    return (
      <div className={`vwap-entry-conditions-bar loading ${className}`}>
        <div className="vwap-band-gauge" />
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const {
    current_price,
    vwap,
    vwap_upper_band,
    vwap_lower_band,
    vwap_deviation_pct,
    band_sigma,
    vwap_period,
    entry_conditions,
  } = data;

  const markerPercent = calculateMarkerPercent(current_price, vwap_lower_band, vwap_upper_band);

  const isBelowLowerBand = entry_conditions?.is_below_lower_band === true;

  return (
    <div className={`vwap-entry-conditions-bar ${className}`}>
      <div className="vwap-entry-condition-row">
        <span className="vwap-entry-condition-label">Entry Condition:</span>
        <span
          className={`vwap-status-chip ${isBelowLowerBand ? 'met' : 'unmet'}`}
          role="status"
          aria-label={`Entry condition ${isBelowLowerBand ? 'met' : 'not met'}: price ${isBelowLowerBand ? 'is' : 'is not'} below lower band`}
        >
          <span className="vwap-status-chip-symbol">{isBelowLowerBand ? '✓' : '✗'}</span>
          Below Lower Band
        </span>
      </div>

      <div className="vwap-band-gauge" role="img" aria-label="VWAP band gauge showing current price relative to lower band, VWAP, and upper band">
        {/* Band ticks: lower (0%) / vwap (50%) / upper (100%) */}
        <div className="vwap-band-tick lower-band" style={{ left: '0%' }}>
          <span className="vwap-band-tick-label">L {formatPrice(vwap_lower_band)}</span>
        </div>
        <div className="vwap-band-tick vwap-mid" style={{ left: '50%' }}>
          <span className="vwap-band-tick-label">VWAP {formatPrice(vwap)}</span>
        </div>
        <div className="vwap-band-tick upper-band" style={{ left: '100%' }}>
          <span className="vwap-band-tick-label">U {formatPrice(vwap_upper_band)}</span>
        </div>

        {/* Current-price marker — the purple line. The tooltip label was
            dropped because it overlapped the entry-condition row above the
            gauge; the canonical numeric readout lives in the Price cell below. */}
        {markerPercent !== null && (
          <div className="vwap-price-marker" style={{ left: `${markerPercent}%` }} />
        )}
      </div>

      {/* Numeric row: how stretched price is right now (Deviation) plus the two
          parameters that define how the bands were computed (Window, Sigma).
          Price is intentionally omitted — it's the headline metric at the top of
          the trade group card and would be redundant here. */}
      <div className="vwap-numeric-row">
        <div className="vwap-numeric-cell">
          <span className="vwap-numeric-label">Deviation</span>
          <span className="vwap-numeric-value">{formatDeviation(vwap_deviation_pct)}</span>
        </div>
        <div className="vwap-numeric-cell">
          <span className="vwap-numeric-label">Window</span>
          <span className="vwap-numeric-value">
            {vwap_period === null || vwap_period === undefined ? '—' : vwap_period}
          </span>
        </div>
        <div className="vwap-numeric-cell">
          <span className="vwap-numeric-label">Band σ</span>
          <span className="vwap-numeric-value">
            {band_sigma === null || band_sigma === undefined ? '—' : band_sigma.toFixed(1)}
          </span>
        </div>
      </div>
    </div>
  );
};

export default VwapEntryConditionsBar;
